import { Link } from 'react-router-dom';

const STARS = Array.from({ length: 40 }, (_, i) => ({
  cx: `${(i * 73 + 17) % 100}%`,
  cy: `${(i * 47 + 11) % 100}%`,
  r: i % 5 === 0 ? 2 : i % 3 === 0 ? 1.5 : 1,
  delay: `${(i * 0.17) % 3}s`,
  dur: `${2 + (i % 3)}s`,
}));

function StarField() {
  return (
    <svg
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      {STARS.map((s, i) => (
        <circle
          key={i} cx={s.cx} cy={s.cy} r={s.r} fill="#FFE66D"
          style={{ animation: `paa-twinkle ${s.dur} ease-in-out ${s.delay} infinite`, opacity: 0.6 }}
        />
      ))}
    </svg>
  );
}

const FLOAT_PLANETS = [
  { symbol: '☀', color: '#FFD700', top: '15%', left: '10%', delay: '0s', dur: '7s' },
  { symbol: '☽', color: '#E0E0E0', top: '20%', right: '8%', delay: '-2.5s', dur: '9s' },
  { symbol: '♀', color: '#F48FB1', top: '65%', left: '5%', delay: '-1.5s', dur: '8s' },
  { symbol: '♂', color: '#EF9A9A', top: '72%', right: '12%', delay: '-4s', dur: '11s' },
  { symbol: '♃', color: '#FFCC80', top: '42%', right: '4%', delay: '-3s', dur: '10s' },
];

function IllustrationRight() {
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '340px', aspectRatio: '1', margin: '0 auto' }}>
      {/* Natal wheel backdrop */}
      <svg viewBox="0 0 340 340" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
        <defs>
          <radialGradient id="paa-glow" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#4A2D8F" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0A0416" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="170" cy="170" r="145" fill="url(#paa-glow)" />
        {[145, 120, 88, 55].map((r, i) => (
          <circle key={i} cx="170" cy="170" r={r} fill="none" stroke="#D4AF37" strokeWidth={i === 0 ? 1 : 0.5} opacity={0.2 + i * 0.08} />
        ))}
        {Array.from({ length: 12 }, (_, i) => {
          const a = (i * 30 - 90) * Math.PI / 180;
          return (
            <line key={i}
              x1={170 + 55 * Math.cos(a)} y1={170 + 55 * Math.sin(a)}
              x2={170 + 145 * Math.cos(a)} y2={170 + 145 * Math.sin(a)}
              stroke="#D4AF37" strokeWidth="0.5" opacity="0.2"
            />
          );
        })}
        {['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'].map((g, i) => {
          const a = ((i * 30 + 15) - 90) * Math.PI / 180;
          return (
            <text key={i} x={170 + 133 * Math.cos(a)} y={170 + 133 * Math.sin(a)}
              textAnchor="middle" dominantBaseline="middle"
              fontSize="11" fill="#D4AF37" opacity="0.55" fontFamily="serif"
            >{g}</text>
          );
        })}
      </svg>

      {/* Floating planets */}
      {FLOAT_PLANETS.map((p, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            ...(p.top ? { top: p.top } : {}),
            ...(('left' in p) ? { left: (p as {left:string}).left } : {}),
            ...(('right' in p) ? { right: (p as {right:string}).right } : {}),
            fontSize: '20px',
            color: p.color,
            textShadow: `0 0 10px ${p.color}`,
            animation: `paa-float ${p.dur} ease-in-out ${p.delay} infinite`,
          }}
        >{p.symbol}</span>
      ))}

      {/* Central ? orb */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '72px', height: '72px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(circle, #4A2D8F, #0A0416)',
        border: '2px solid rgba(212,175,55,0.7)',
        borderRadius: '50%',
        boxShadow: '0 0 28px rgba(212,175,55,0.45), inset 0 0 16px rgba(212,175,55,0.15)',
      }}>
        <span style={{ fontSize: '34px', color: '#D4AF37', fontFamily: 'var(--font-display)', fontStyle: 'italic', lineHeight: 1 }}>?</span>
      </div>
    </div>
  );
}

export function PergunteAosAstrosSpotlight() {
  return (
    <section
      id="pergunte-destaque"
      style={{
        position: 'relative',
        padding: 'clamp(5rem, 10vw, 7.5rem) 2rem',
        background: 'radial-gradient(ellipse 80% 70% at 50% 40%, #2D1B69 0%, #1A0B2E 55%, #0A0416 100%)',
        overflow: 'hidden',
      }}
    >
      <StarField />

      <style>{`
        @keyframes paa-twinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.8); }
          50% { opacity: 0.95; transform: scale(1.3); }
        }
        @keyframes paa-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-18px); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="paa-twinkle"], [style*="paa-float"] { animation: none !important; }
        }
        @media (max-width: 1023px) {
          .paa-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 639px) {
          .paa-illustration { display: none !important; }
        }
      `}</style>

      <div
        className="paa-grid"
        style={{
          position: 'relative', zIndex: 1,
          maxWidth: '1100px', margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '3fr 2fr',
          gap: 'clamp(3rem, 6vw, 5rem)',
          alignItems: 'center',
        }}
      >
        {/* Copy */}
        <div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            marginBottom: '1.75rem',
            padding: '0.35rem 1rem',
            border: '1px solid rgba(212,175,55,0.45)',
            borderRadius: '100px',
          }}>
            <span style={{
              fontSize: '11px', color: '#D4AF37',
              letterSpacing: '0.22em', textTransform: 'uppercase',
              fontFamily: 'var(--font-body)', fontWeight: 700,
            }}>✦ Nosso diferencial</span>
          </div>

          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.9rem, 4.2vw, 3.1rem)',
            fontWeight: 700, color: '#ffffff',
            margin: '0 0 1.25rem', lineHeight: 1.15,
          }}>
            Pergunte aos Astros: consulta personalizada, resposta na hora
          </h2>

          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(0.95rem, 1.8vw, 1.05rem)',
            color: 'rgba(255,255,255,0.72)', lineHeight: 1.8,
            margin: '0 0 2rem', maxWidth: '520px',
          }}>
            Não é horóscopo genérico. É uma resposta precisa baseada no seu mapa astral completo
            e nas posições planetárias atuais e futuras. Como consultar um astrólogo experiente,
            disponível 24 horas por dia.
          </p>

          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 2.5rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            {[
              'Respostas precisas sobre amor, carreira e decisões importantes, não frases genéricas de signo solar',
              'Baseado no seu mapa natal completo + trânsitos planetários atuais, análise única para você',
              'Disponível a qualquer hora, sem agendamento, sem espera',
            ].map((bullet, i) => (
              <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <span style={{ color: '#D4AF37', fontSize: '1rem', flexShrink: 0, marginTop: '2px' }}>✦</span>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-15)', color: 'rgba(255,255,255,0.78)', lineHeight: 1.65 }}>
                  {bullet}
                </span>
              </li>
            ))}
          </ul>

          <Link
            to="/register?service=pergunte-aos-astros"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0 2rem', height: '60px',
              background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #D4AF37 100%)',
              color: '#0A0416',
              fontFamily: 'var(--font-body)', fontSize: 'var(--text-15)', fontWeight: 700,
              textDecoration: 'none', borderRadius: '12px',
              letterSpacing: '0.02em',
              boxShadow: '0 0 28px rgba(212,175,55,0.35)',
              transition: 'box-shadow 0.25s ease, transform 0.2s ease',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 50px rgba(212,175,55,0.65)';
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 28px rgba(212,175,55,0.35)';
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
            }}
          >
            ✦ Fazer minha primeira pergunta
          </Link>
        </div>

        {/* Illustration */}
        <div className="paa-illustration" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IllustrationRight />
        </div>
      </div>
    </section>
  );
}
